import React, { useCallback, useEffect, useState } from "react"
import { Outlet } from "react-router-dom"
import { Layout } from "antd"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import { isEqual, omit } from "lodash"
import useSearchFilter from "hooks/useSearchFilter"
import moment from "moment"
import { useDispatch } from "react-redux"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./Calendar.module.css"
import Header from "./Header"
import CalendarViewSwitcher from "./CalendarViewSwitcher"
import { getCalendarBookings } from "store/calendar"
import { ExitScheduleTimeOff, Journal, ScheduleTimeOff } from "./Modals"
import { useCalendarDateRange } from "./useCalendarDateRange"
function Calendar() {
  const dispatch = useDispatch()
  const [params, updateSearchFilters] = useSearchFilter({
    accessAttributes: ["range", "date", "weather", "trip_id", "journal_date", "unavailable_id"]
  })
  const [paramsState, setParamsState] = useState(params)
  // Consolidated modal state for maintainability
  const [modals, setModals] = useState({ scheduleTimeOff: false, exitScheduleTimeOff: false, journal: false })
  // Preserve existing setter APIs via thin wrappers
  const setIsScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, scheduleTimeOff: open }))
  const setIsExitScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, exitScheduleTimeOff: open }))
  const setIsJournalModalOpen = (open) => setModals((m) => ({ ...m, journal: open }))

  const dates = useCalendarDateRange(params)

  const changeHandler = useCallback(
    ({ target, extra }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value,
        ...(extra && { [extra.name]: extra.value })
      })),
    []
  )

  useEffect(() => {
    if (isEqual(paramsState, omit(params, ["journal_date", "unavailable_id"]))) return
    updateSearchFilters(paramsState)
  }, [params, paramsState, updateSearchFilters])

  useEffect(() => {
    if (!dates?.start || !dates?.end) return

    const data = {
      "q[date_gteq]": dates.start,
      "q[date_lteq]": dates.end
    }

    dispatch(getCalendarBookings(data))
  }, [dates?.start, dates?.end, dispatch])

  useEffect(() => {
    if (!dates?.start || !dates?.end) return
    const data = {}
    const startMoment = moment(dates.start)
    const endMoment = moment(dates.end)

    if (params.trip_id) data.trip_id = params.trip_id

    data.forecast_date = []
    while (startMoment.isSameOrBefore(endMoment)) {
      data.forecast_date.push(startMoment.format(DATE_FORMAT))
      startMoment.add(1, "day")
    }
  }, [dates?.start, dates?.end, params?.trip_id, params?.weather, dispatch])

  const handleCancelScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(true)
  }
  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleCancelExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
  }

  const handleYesExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
    setIsScheduleTimeOffModalOpen(false)
  }

  const handleJournalClick = () => {
    setIsJournalModalOpen(true)
  }

  return (
    <>
      <Layout className={styles.container} id="dashboard">
        <div className={styles.header}>
          <Header
            params={params}
            onChangeFilters={changeHandler}
            handleJournalClick={handleJournalClick}
            setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
          />
        </div>
        <div className={styles.calendarWrapper}>
          <div className={styles.calendar}>
            <CalendarViewSwitcher onChangeFilters={changeHandler} />
          </div>
        </div>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </Layout>
      {modals.journal && (
        <Journal
          open={modals.journal}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={params.date || moment().format(DATE_FORMAT)}
          startDate={params.date || moment().format(DATE_FORMAT)}
          endDate={params.date || moment().format(DATE_FORMAT)}
        />
      )}
      {modals.scheduleTimeOff && (
        <ScheduleTimeOff
          dates={dates}
          open={modals.scheduleTimeOff}
          onCancel={handleCancelScheduleTimeOffModal}
          setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
        />
      )}
      {modals.exitScheduleTimeOff && (
        <ExitScheduleTimeOff
          open={modals.exitScheduleTimeOff}
          onCancel={handleCancelExitScheduleTimeOffModal}
          onYes={handleYesExitScheduleTimeOffModal}
        />
      )}
    </>
  )
}

export default Calendar
